import { KeyboardEvent } from 'react';
import { useSelector } from 'react-redux';
import { RupeeIcon } from '@icons';
import clsx from 'clsx';

import testId from '@constants/testId';
import useCurrency, { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';
import { sanitizeNumberInput } from '@utils/generic';
import { AmountInputProps } from 'types/input';

import { inputStyles, sizeStyle } from '../input/input.config';

const withRupeeGlyph = (message: string) => {
  const [before, after] = message.split('₹');
  if (after === undefined) return message;
  return (
    <span>
      {before}
      <span className="font-inter">₹</span>
      {after}
    </span>
  );
};

const AmountInput = (props: AmountInputProps) => {
  const {
    className,
    name,
    type = 'text',
    inputClassNames,
    inputSize = 'medium',
    value,
    disabled,
    placeholder = '0',
    onAmountChange,
    maxAmount,
    error,
    errorMessage,
    handleInputBlur,
    allowZero,
    onClick,
    allowDecimals = false,
    useDefaultCurrencyIcon = false,
    suffixText = '',
    customInputStyle = '',
    ...rest
  } = props;

  const { formatCurrencyValue } = useCurrency();

  const { currencyConfig } = useSelector((state: any) => state.user);

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const handleOnClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    // Prevent the default behavior for arrow keys
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    // Prevent value incrementing / decrementing on scroll
    e.preventDefault();
    e.currentTarget.blur();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // Determine allowed pattern based on the allowDecimals prop
    const regex = allowDecimals ? /^[0-9]*\.?[0-9]{0,2}$/ : /^[0-9]*$/;

    const inputValue = event.target.value;

    if (!regex.test(inputValue)) {
      return;
    }

    const amount = sanitizeNumberInput(inputValue, allowDecimals);

    const integerPart = amount.split('.')[0] || '';
    if (integerPart.length > 10) return;

    let formattedAmount = amount;

    if (
      !allowZero &&
      amount !== '' &&
      (Number(formattedAmount) <= 0 ||
        +Number(formattedAmount).toFixed(2) > +Number(maxAmount).toFixed(2))
    ) {
      formattedAmount = '';
      return;
    }

    formattedAmount = amount;
    onAmountChange(formattedAmount);
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (handleInputBlur) {
      const inputValue = e.target.value.trim();
      handleInputBlur(inputValue);
    }
  };

  return (
    <div
      className={`${
        inputStyles.common
      } relative flex items-center rounded-lg px-4 py-2.5 ${
        error ? `${inputStyles.error}` : 'hover:focus-within:border-primary'
      } ${sizeStyle[inputSize]} ${className}`}
      onClick={onClick}
      onKeyDown={() => null}
      role="presentation"
    >
      <div
        className={`flex h-5 w-3.5 items-center justify-center font-inter text-cofeeNeutral10 ${
          isDefaultCurrency ? 'text-xl' : 'text-sm font-medium'
        } font-light`}
      >
        {useDefaultCurrencyIcon ? (
          <RupeeIcon className="h-5 w-3.5" />
        ) : (
          formatCurrencyValue(Number(value)).symbol
        )}
      </div>
      <div className="h-9 border border-transparent border-r-grey40 pr-3.5" />
      <input
        id={name}
        name={name}
        type={type}
        className={clsx(
          inputClassNames,
          inputStyles,
          'no-spinners w-full bg-transparent pl-3 focus:outline-none',
          customInputStyle
        )}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
        onClick={onClick || handleOnClick}
        onChange={handleInputChange}
        onBlur={onInputBlur}
        {...rest}
      />
      {suffixText && (
        <div className="absolute right-4 text-sm text-cofeeNeutral7">
          {suffixText}
        </div>
      )}
      {error && errorMessage && (
        <div
          className="absolute -bottom-5 left-0 flex items-center text-xs text-error"
          data-testid={testId.amountInput.errorMessage}
        >
          {typeof errorMessage === 'string'
            ? withRupeeGlyph(errorMessage)
            : errorMessage}
        </div>
      )}
    </div>
  );
};

export default AmountInput;
